import React from "react";
import { YardDateField } from "../../../components/common/YardDateField";
import { useHistory } from "react-router-dom";

import { Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  ArrayField,
  BooleanField,
  Datagrid,
  FileField,
  FunctionField,
  Labeled,
  NumberField,
  ReferenceField,
  Show,
  SimpleShowLayout,
  TextField,
  TopToolbar,
  useShowController,
} from "react-admin";

import { BackButton } from "../../../components/common/BackButton";
import {
  CustomField,
  CustomFieldsMobile,
} from "../../../components/common/CustomFields";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { FormSection } from "../../../components/common/FormSection";
import CustomizableDatagrid from "../../../components/common/ra-customizable-datagrid";
import { SendPOSDialogButton } from "../../../components/common/SendPOSDialogButton";

import { CarrierType, InboundStatus } from "../../../utils/constants";
import { getBackPath, quantityRenderer } from "../../../utils/utils";

import { InboundAside } from "./InboundAside";
import { NotesTabShow } from "../../../components/common/NotesTab";
import {
  calcTotalQuantity,
  getPODEmailSubject,
  handleSendPOD,
  renderTotalNetWeight,
} from "../../../managerApp/resources/inboundShipments/InboundUtils";

const useStyles = makeStyles({
  alignRight: {
    textAlign: "right",
  },
});

const QuantityWithUnitsRenderer = ({ record, source, units }) => {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <NumberField record={record} source={source} />
      &nbsp;{record[units]}
    </div>
  );
};

/**
 * Exception flags the aside renders. Shared with the dispatch edit form, which
 * reuses this show's aside.
 */
export const getInboundExceptions = (record) =>
  !record
    ? {}
    : {
        hasException: record.hasException,
        unsafeShipment: record.unsafeShipment,
        unsafeShipmentNote: record.unsafeShipmentNote,
        brokenSeal: record.brokenSeal,
        brokenSealNote: record.brokenSealNote,
        otherException: record.otherException,
        otherExceptionNote: record.otherExceptionNote,
      };

export const ShipmentSetupTab = (props) => {
  const { record } = props;

  return (
    <Grid container direction="column" spacing={1}>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={6}>
          <Labeled label="Account">
            <TextField source="accountName" />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={6}>
          <Labeled label="Origin">
            <TextField source="shipFrom" />
          </Labeled>
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={6}>
          <Labeled label="PO Number">
            <TextField source="poNumber" />
          </Labeled>
        </Grid>
      </Grid>
      <Grid container item direction="row" spacing={1}>
        <Grid item xs={12} md={6}>
          <Labeled label="Load Type">
            <ReferenceField
              {...props}
              source="reloadYardCommodityId"
              reference="reloadYardCommoditys"
              link={false}
            >
              <TextField source="commodity.name" />
            </ReferenceField>
          </Labeled>
        </Grid>
        <Grid item xs={12} md={6}>
          <Labeled label="Pickup Area">
            <ReferenceField
              {...props}
              source="areaId"
              reference="areas"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
          </Labeled>
        </Grid>
      </Grid>
      {/* The yard's delivery instructions — there is no other channel for
          them, so a carrier that cannot see them here sends a driver without
          whatever they say. */}
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12}>
          <Labeled label="Special Instructions">
            <TextField source="instructions" multiline />
          </Labeled>
        </Grid>
      </Grid>
      {record.customFields && (
        <CustomFieldsMobile
          recordFields={record.customFields}
          accountFields={record.account?.inboundCustomFields}
          width={6}
        />
      )}
      {record?.account?.trackInboundSeals && (
        <Grid container xs={12} md={6}>
          <Labeled label="Seals" fullWidth>
            <ArrayField source="seals.items">
              <Datagrid>
                <BooleanField source="validated" />
                <TextField source="barcode" />
                <TextField source="discribtion" />
              </Datagrid>
            </ArrayField>
          </Labeled>
        </Grid>
      )}
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={6}>
          <Labeled label="ATTACHMENTS">
            <FileField
              source="attachments"
              src="src"
              title="title"
              target="_blank"
            />
          </Labeled>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12}></Grid> &nbsp;{" "}
      </Grid>
    </Grid>
  );
};

// Carriers see the tally sheet's top-level quantities only — no drill-in to the
// full inventory record.
export const TallySheetTab = (props) => {
  const classes = useStyles();
  const { record } = props;
  const account = record.account;

  const accountFields =
    account?.inventoryCustomFields?.length > 0
      ? account.inventoryCustomFields
      : [];
  const customColumns = [];
  const customGridFields = accountFields
    ? accountFields.map((field, index) => {
        customColumns.push(field.name);
        return (
          <CustomField
            source={`customFields[${index}]`}
            field={field}
            label={field.name}
            sortable={true}
          />
        );
      })
    : [];

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item xs={12}>
        <ArrayField
          source="inventories.items"
          basePath="/inventories"
          fullWidth
        >
          <CustomizableDatagrid
            label="INBOUND INVENTORY"
            hasBulkActions={false}
            storageProp={`InboundShoww_${account?.id}`}
            defaultColumns={[
              "productName",
              "quantityAdvised",
              "quantityReceived",
              ...customColumns,
            ]}
          >
            {[
              <TextField source="productName" label="Product Name" />,
              <FunctionField
                label="Qty. Advised"
                render={(record) => (
                  <QuantityWithUnitsRenderer
                    record={record}
                    source="quantityAdvised"
                    units="inventoryUOM"
                  />
                )}
                headerClassName={classes.alignRight}
                cellClassName={classes.alignRight}
              />,
              <FunctionField
                label="Qty. Received"
                render={(record) => (
                  <QuantityWithUnitsRenderer
                    record={record}
                    source="quantityReceived"
                    units="inventoryUOM"
                  />
                )}
                headerClassName={classes.alignRight}
                cellClassName={classes.alignRight}
              />,
              ...customGridFields,
            ]}
          </CustomizableDatagrid>
        </ArrayField>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="overline" color="textSecondary">
          TOTAL LOAD
        </Typography>
        <Grid container direction="row" spacing={1}>
          <Grid item xs={6} md={3}>
            <Labeled label="Total Quantity">
              <FunctionField
                render={(record) =>
                  quantityRenderer(calcTotalQuantity(record), record.totalUOM)
                }
              />
            </Labeled>
          </Grid>
          <Grid item xs={6} md={3}>
            <Labeled label="Total Weight">
              <FunctionField
                render={(record) => renderTotalNetWeight(record)}
              />
            </Labeled>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

// There is a gross weight only once the truck has crossed the scale both ways.
// With either reading still missing the field stays blank: the old fallback
// printed "0", and an unset unit interpolated as the word "null" beside it, so
// an unweighed truck looked like a weighed empty one. The unit is appended only
// when it is known.
const grossRender = (record) => {
  const { tareWeight, ladenWeight } = record;

  if (
    tareWeight == null ||
    tareWeight < 0 ||
    ladenWeight == null ||
    ladenWeight < 0
  )
    return "";

  const units = record.weightUnits || record.scaleWeightUnits;

  return `${(ladenWeight - tareWeight).toLocaleString()}${
    units ? ` ${units}` : ""
  }`;
};

export const ShipmentTraceTab = (props) => {
  const { record } = props;

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item xs={12} md={4}>
        <Labeled label="Time Scheduled">
          <YardDateField source="scheduledDateTime" showTime />
        </Labeled>
      </Grid>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={4}>
          <Labeled label="Time Arrived">
            <YardDateField source="notifyDateTime" showTime />
          </Labeled>
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={4}>
          <Labeled label="Time Placed">
            <YardDateField source="placedDateTime" showTime />
          </Labeled>
        </Grid>
        {record.carrierType === CarrierType.TRUCK && (
          <>
            <Grid item xs={12} md={4}>
              <Labeled label="Laden Weight">
                <NumberField source="ladenWeight" />
              </Labeled>
            </Grid>
            <Grid item xs={12} md={4}>
              <Labeled label="Units">
                <TextField source="scaleWeightUnits" />
              </Labeled>
            </Grid>
          </>
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        <Labeled label="Unload Start Time">
          <YardDateField source="startDateTime" showTime />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={4}>
        <Labeled label="Unload End Time">
          <YardDateField source="endDateTime" showTime />
        </Labeled>
      </Grid>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={4}>
          <Labeled label="Time Released">
            <YardDateField source="releaseDateTime" showTime />
          </Labeled>
        </Grid>
        {record.carrierType === CarrierType.TRUCK && (
          <>
            <Grid item xs={12} md={2}>
              <Labeled label="Tare Weight">
                <NumberField source="tareWeight" />
              </Labeled>
            </Grid>
            <Grid item xs={12} md={2}>
              <Labeled label="Gross Weight">
                <FunctionField render={grossRender} />
              </Labeled>
            </Grid>
            <Grid item xs={12} md={2}>
              <Labeled label="Net Weight">
                <NumberField source="netWeight" />
              </Labeled>
            </Grid>
          </>
        )}
      </Grid>
      {record.carrierType === CarrierType.RAIL && (
        <Grid container direction="row" spacing={1}>
          <Grid item xs={12} md={4}>
            <Labeled label="Time Collected">
              <YardDateField source="collectedDateTime" showTime />
            </Labeled>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const ShowActions = ({ data, ...props }) => (
  <TopToolbar>
    {data && !data.status?.startsWith(InboundStatus.ADVICE_ALL) && (
      <SendPOSDialogButton
        label="SEND"
        title="Send POD"
        record={data}
        email={data.account?.podEmail}
        subjectDefault={getPODEmailSubject(data)}
        enclosePhotosDefault={data.account?.podPhotos}
        encloseDocsDefault={data.account?.podAttachments}
        handleSend={handleSendPOD}
      />
    )}
    <BackButton {...props} />
  </TopToolbar>
);

export const InboundShow = (props) => {
  const { record } = useShowController(props);
  const backPath = getBackPath(props);
  const history = useHistory();
  const exceptions = getInboundExceptions(record);

  return (
    <Show
      {...props}
      title={<ForceTitle title="View Inbound" />}
      actions={<ShowActions {...props} backPath={backPath} />}
      aside={
        <InboundAside
          {...props}
          history={history}
          backPath={backPath}
          exceptions={exceptions}
        />
      }
    >
      {/* One page, one section per former tab: a carrier reads the delivery
          top to bottom instead of hunting for which tab holds the field they
          came for. Tally sheet is yard-side detail; carriers never see it. */}
      <SimpleShowLayout>
        <FormSection title="Inbound Info">
          <ShipmentSetupTab {...props} />
        </FormSection>
        <FormSection title="Dispatch Events">
          <ShipmentTraceTab />
        </FormSection>
        <FormSection title="Yard Notes">
          <NotesTabShow {...props} hidePrivate={true} hideLabel={true} />
        </FormSection>
      </SimpleShowLayout>
    </Show>
  );
};
